import type { CreateObraBody, ObraDetail } from '@obras/shared';
import { formatCurrency, maskCnpj, parseCurrencyToCents } from '@obras/shared';
import { useMemo } from 'react';

import type { MultiSelectOption } from '../../components/ui/MultiSelect.js';
import { useSecretariaOptions } from '../secretarias/api.js';
import { useOrgMembers } from '../usuarios/api.js';
import { useCompanyOptions } from './api.js';

/*
 * O formulário da obra, comum à página de cadastro (`NovaObraPage`) e ao modal
 * de edição (`ObraFormModal`): o estado, como ele nasce, como vira corpo de
 * requisição e as opções dos seletores. Os dois mostram os campos de jeitos
 * diferentes — um em etapas guiadas, o outro em sumário —, mas o que se guarda e
 * o que se manda ao servidor é um só.
 */

export interface FormularioDaObra {
  name: string;
  description: string;
  contractNumber: string;
  initialValue: string;
  status: string;
  isReform: boolean;
  secretariaId: string;
  companyId: string;
  responsibleFiscalId: string;
  contractStart: string;
  contractEnd: string;
  osStart: string;
  osEnd: string;
  visitIntervalDays: string;
  coverMediaId: string;
  latitude: string;
  longitude: string;
  address: string;
  city: string;
  state: string;
}

export function formularioInicial(
  obra?: ObraDetail,
  padroes: { fiscal?: string | undefined; intervalo?: number | undefined } = {},
): FormularioDaObra {
  return {
    name: obra?.name ?? '',
    description: obra?.description ?? '',
    contractNumber: obra?.contractNumber ?? '',
    initialValue: obra ? formatCurrency(obra.initialValueCents) : '',
    status: obra?.status ?? 'em_andamento',
    isReform: obra?.isReform ?? false,
    secretariaId: obra?.secretaria?.id ?? '',
    companyId: obra?.company?.id ?? '',
    responsibleFiscalId: obra?.responsibleFiscal?.id ?? padroes.fiscal ?? '',
    contractStart: obra?.contractStart ?? '',
    contractEnd: obra?.contractEnd ?? '',
    osStart: obra?.osStart ?? '',
    osEnd: obra?.osEnd ?? '',
    visitIntervalDays: obra
      ? String(obra.visitIntervalDays)
      : padroes.intervalo
        ? String(padroes.intervalo)
        : '',
    coverMediaId: obra?.coverMediaId ?? '',
    latitude: obra?.latitude != null ? String(obra.latitude) : '',
    longitude: obra?.longitude != null ? String(obra.longitude) : '',
    address: obra?.address ?? '',
    city: obra?.city ?? '',
    state: obra?.state ?? '',
  };
}

/** Campo vazio vira `undefined`: o backend distingue "não informado" de "vazio". */
export const orUndefined = (value: string): string | undefined =>
  value.trim() === '' ? undefined : value;

export const numberOrUndefined = (value: string): number | undefined => {
  const trimmed = value.trim().replace(',', '.');
  if (trimmed === '') return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/** Coordenada digitada à mão, para o mapa acompanhar. `NaN` não vira ponto. */
export const coordOrNull = (value: string): number | null => {
  const parsed = Number(value.trim().replace(',', '.'));
  return value.trim() !== '' && Number.isFinite(parsed) ? parsed : null;
};

/** O corpo do `POST /obras` e do `PATCH /obras/:id`, a partir do que está na tela. */
export function corpoDaObra(form: FormularioDaObra): CreateObraBody {
  return {
    name: form.name.trim(),
    description: orUndefined(form.description),
    isReform: form.isReform,
    secretariaId: orUndefined(form.secretariaId),
    contractNumber: form.contractNumber.trim(),
    companyId: orUndefined(form.companyId),
    responsibleFiscalId: orUndefined(form.responsibleFiscalId),
    status: form.status as CreateObraBody['status'],
    initialValueCents: parseCurrencyToCents(form.initialValue) ?? 0,
    contractStart: orUndefined(form.contractStart),
    contractEnd: orUndefined(form.contractEnd),
    osStart: orUndefined(form.osStart),
    osEnd: orUndefined(form.osEnd),
    visitIntervalDays: numberOrUndefined(form.visitIntervalDays),
    coverMediaId: orUndefined(form.coverMediaId),
    latitude: numberOrUndefined(form.latitude),
    longitude: numberOrUndefined(form.longitude),
    address: orUndefined(form.address),
    city: orUndefined(form.city),
    state: orUndefined(form.state),
  };
}

/**
 * As opções dos três seletores da obra — empresa, secretaria e fiscal —, com o
 * que cada uma mostra e por onde se acha: a empresa também pelo CNPJ, que
 * ninguém decora mas está impresso no contrato; a secretaria também pela sigla.
 */
export function useOpcoesDaObra() {
  const companies = useCompanyOptions();
  const secretarias = useSecretariaOptions();
  const members = useOrgMembers();

  const empresas: MultiSelectOption[] = useMemo(
    () =>
      (companies.data?.items ?? []).map((empresa) => ({
        value: empresa.id,
        label: empresa.tradeName ?? empresa.legalName,
        detail: empresa.tradeName ? empresa.legalName : (empresa.cnpj ?? undefined),
        terms: empresa.cnpj ? [empresa.cnpj, maskCnpj(empresa.cnpj)] : undefined,
      })),
    [companies.data],
  );

  const secretariasOpcoes: MultiSelectOption[] = useMemo(
    () =>
      (secretarias.data?.items ?? []).map((item) => ({
        value: item.id,
        label: item.acronym ? `${item.acronym} — ${item.name}` : item.name,
        detail: item.description ?? undefined,
        terms: item.acronym ? [item.acronym] : undefined,
      })),
    [secretarias.data],
  );

  const fiscais: MultiSelectOption[] = useMemo(
    () =>
      (members.data?.items ?? []).map((membro) => ({
        value: membro.id,
        label: membro.name,
        detail: membro.email ?? undefined,
      })),
    [members.data],
  );

  return {
    empresas,
    secretarias: secretariasOpcoes,
    fiscais,
    /** As empresas como vieram, para a ficha da escolhida (razão social, CNPJ, contato). */
    empresasCompletas: companies.data?.items ?? [],
    /** Os membros do órgão como vieram, para a equipe (cargo, avatar). */
    membros: members.data?.items ?? [],
    carregando: {
      empresas: companies.isLoading,
      secretarias: secretarias.isLoading,
      fiscais: members.isLoading,
    },
  };
}
